import { InfoCircleOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import Modal from 'antd/es/modal/Modal';
import React, { useContext } from 'react';

import { CurrencyContext } from 'app/components/commons/Currency/CurrencyContext/CurrencyContext';
import { BabyBedIcon } from 'app/components/commons/icons/BabyBedIcon';
import { BedIcon } from 'app/components/commons/icons/BedIcon';
import { BABY_COT_BED_CATEGORY_ID } from 'app/typings';
import { pluralize } from 'app/utils/strings';

import type { Room } from '../../ExtranetPackages.types';

import styles from './RoomModal.module.scss';

type Bed = NonNullable<Room['beds']>[number];

const ADDITIONAL_BED_TOOLTIP =
  'Fees for preparing the additional bed (bed linens, house keeping, etc). This does not include any fees related to your Staycation package.';

type BedItemProps = {
  bed: Bed;
  showPrice?: boolean;
};

const BedItem: React.FC<BedItemProps> = ({ bed, showPrice }) => {
  const isBabyCot = bed.categoryId === BABY_COT_BED_CATEGORY_ID;
  const { currencyFormatter } = useContext(CurrencyContext);

  return (
    <div className={styles.bedItem}>
      <div className={styles.bedIcon}>
        {isBabyCot ? <BabyBedIcon /> : <BedIcon />}
      </div>
      <div className={styles.bedContent}>
        <div className={styles.bedName}>{bed.name}</div>
        <div className={styles.bedGuests}>
          {isBabyCot
            ? pluralize(bed.capacity.baby, 'baby', 'babies')
            : pluralize(bed.capacity.guest, 'guest', 'guests')}
        </div>
      </div>
      {showPrice && (
        <div className={styles.bedPrice}>
          {currencyFormatter(bed.price)} per night
          <Tooltip title={ADDITIONAL_BED_TOOLTIP}>
            <InfoCircleOutlined className={styles.bedPriceIcon} />
          </Tooltip>
        </div>
      )}
    </div>
  );
};

type BedSectionProps = {
  title: string;
  beds: Bed[];
  showPrice?: boolean;
};

const BedSection: React.FC<BedSectionProps> = ({ title, beds, showPrice }) => {
  if (beds.length === 0) {
    return null;
  }

  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>{title}</div>
      <div className={styles.bedsList}>
        {beds.map((bed, index) => (
          <BedItem
            key={`${bed.name}-${index}`}
            bed={bed}
            showPrice={showPrice}
          />
        ))}
      </div>
    </div>
  );
};

type RoomModalProps = {
  isModalOpen: boolean;
  onClose: () => void;
  room?: Room;
};

export const RoomModal = ({ isModalOpen, onClose, room }: RoomModalProps) => {
  if (!room) {
    return null;
  }

  const defaultBeds =
    room.beds?.filter((bed) => bed.isIncluded !== false) ?? [];
  const additionalBeds =
    room.beds?.filter((bed) => bed.isIncluded === false) ?? [];

  return (
    <Modal
      title={room.name}
      open={isModalOpen}
      onCancel={onClose}
      closable
      destroyOnClose
      className={styles.roomModal}
      footer={[
        <Button key="close" onClick={onClose}>
          Close
        </Button>,
      ]}
    >
      <div className={styles.container}>
        <BedSection title="Default beds" beds={defaultBeds} />
        <BedSection title="Additional" beds={additionalBeds} showPrice />
      </div>
    </Modal>
  );
};
